"use client";

/**
 * FitText — universal auto-shrink text wrapper for Wrapped cards.
 *
 * Why this exists: Wrapped overlay frame is clamped to 450px on desktop
 * (`min(450,100vw)` in WrappedStoriesPlayer). Hardcoded `text-[Xpx]` overflowed
 * for long cyrillic words, $999M-class numbers, 60-char niche names. Constitution
 * §6 mandates fluid sizing. This component does it the simple way: measure
 * after layout, shrink one px at a time until it fits, re-measure on container
 * resize.
 *
 * Usage:
 *   <FitText maxFontSize={64}>$75K/mo</FitText>                       — single line
 *   <FitText maxFontSize={40} multiline minFontSize={20}>{name}</FitText> — wraps but caps height
 *
 * Limits:
 *   - The shrink loop runs O(maxFontSize − minFontSize) px steps. For typical
 *     ranges (16..180) that's ~165 cycles, sub-millisecond.
 *   - Children must produce a single text node when not multiline. Multiline
 *     mode allows arbitrary inline content as long as it's text-like.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

interface FitTextProps {
  children: ReactNode;
  /** Starting (largest) font-size in px. Component never grows beyond this. */
  maxFontSize: number;
  /** Floor in px. Refuses to shrink below this — content may still overflow at min. Default: 12. */
  minFontSize?: number;
  /** Tailwind / utility classes for the inner element (font-family, weight, gradient, color). */
  className?: string;
  /** Inline style for the inner element (textShadow, gradients, etc.). Font-size is overridden. */
  style?: CSSProperties;
  /** Allow line wraps; shrinks until both width AND height fit container. */
  multiline?: boolean;
  /** Element type for the text node. Default: span. Use p/h1/h2 for semantics. */
  as?: ElementType;
  /**
   * Optional fixed container max-height in px. Multiline mode falls back to
   * container.clientHeight, which is 0 if parent is intrinsic-sized — pass
   * `containerMaxHeight` for those cases.
   */
  containerMaxHeight?: number;
}

export function FitText({
  children,
  maxFontSize,
  minFontSize = 12,
  className,
  style,
  multiline = false,
  as: Tag = "span",
  containerMaxHeight,
}: FitTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLElement>(null);
  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  useEffect(() => {
    const container = containerRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;

    const fit = () => {
      const cw = container.clientWidth;
      if (!cw) return;
      const ch = containerMaxHeight ?? container.clientHeight ?? Infinity;

      // Reset to max, then walk down until it fits or hits floor.
      let size = maxFontSize;
      inner.style.fontSize = `${size}px`;
      while (size > minFontSize) {
        const overflowsX = inner.scrollWidth > cw;
        const overflowsY = multiline && ch !== Infinity && inner.scrollHeight > ch;
        if (!overflowsX && !overflowsY) break;
        size -= 1;
        inner.style.fontSize = `${size}px`;
      }
      setFontSize(size);
    };

    fit();

    // Re-fit when the frame width changes (overlay open/close, resize).
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    return () => ro.disconnect();
  }, [children, maxFontSize, minFontSize, multiline, containerMaxHeight]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Tag
        ref={innerRef as React.Ref<HTMLElement>}
        className={className}
        style={{
          ...style,
          fontSize,
          ...(multiline ? {} : { whiteSpace: "nowrap" }),
        }}
      >
        {children}
      </Tag>
    </div>
  );
}
